// ── Prefs (localStorage) ──────────────────────────────────────
import { LS_KEY, LS_SCRIPTS_KEY } from "./constants";

export function loadPrefs() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export function savePrefs(patch) {
  try {
    const prev = loadPrefs();
    localStorage.setItem(LS_KEY, JSON.stringify({ ...prev, ...patch }));
  } catch {}
}

export function loadScriptSlots() {
  try {
    const raw = localStorage.getItem(LS_SCRIPTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function saveScriptSlots(slots) {
  try { localStorage.setItem(LS_SCRIPTS_KEY, JSON.stringify(slots)); } catch {}
}

// ── Script tokeniser ──────────────────────────────────────────
/**
 * Parses raw script text into a flat token array.
 * Token shapes:
 *   { type: "word",    word: string }
 *   { type: "section", label: string }
 *   { type: "note",    text: string }
 */
export function parseScript(raw) {
  const tokens = [];

  const noteStripped = raw.replace(/\[\[([^\]]*)\]\]/g, (_, note) => {
    tokens.push({ type: "note", text: note.trim() });
    return "\x00note\x00";
  });

  noteStripped.split(/\n/).forEach(line => {
    const trimmed = line.trim();
    if (trimmed.startsWith("##")) {
      tokens.push({ type: "section", label: trimmed.slice(2).trim() || "Section" });
      return;
    }
    trimmed.split(/\s+/).forEach(w => {
      if (w && w !== "\x00note\x00") tokens.push({ type: "word", word: w });
    });
  });

  return tokens.filter(t => !(t.type === "word" && !t.word));
}

/** Extracts only the word strings from a token array. */
export function tokensToWords(tokens) {
  return tokens.filter(t => t.type === "word").map(t => t.word);
}

/** Builds a section jump index from a token array. */
export function buildSections(tokens) {
  const sections = [];
  let wordIdx = 0;
  tokens.forEach(t => {
    if (t.type === "section") sections.push({ label: t.label, wordIndex: wordIdx });
    else if (t.type === "word") wordIdx++;
  });
  return sections;
}

/** Returns presenter notes that appear immediately before wordIdx. */
export function notesAtWord(tokens, wordIdx) {
  const notes = [];
  let wi = 0;
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i].type === "note" && wi === wordIdx) notes.push(tokens[i].text);
    if (tokens[i].type === "word") {
      if (wi === wordIdx) break;
      wi++;
    }
  }
  return notes;
}

/** Reconstructs raw script text from a token array. */
export function tokensToRaw(tokens) {
  return tokens
    .map(t => {
      if (t.type === "section") return `\n## ${t.label}\n`;
      if (t.type === "note")    return `[[${t.text}]]`;
      return t.word;
    })
    .join(" ")
    .replace(/ \n/g, "\n")
    .replace(/\n /g, "\n")
    .trim();
}

// ── Script diffing ────────────────────────────────────────────
/**
 * Splits a raw script into logical sections by ## markers.
 * Returns [{ heading: string|null, body: string }]
 */
export function splitIntoSections(raw) {
  const lines  = raw.split("\n");
  const result = [];
  let current  = { heading: null, body: [] };

  for (const line of lines) {
    if (line.trim().startsWith("##")) {
      if (current.body.join("").trim() || current.heading != null) {
        result.push({ heading: current.heading, body: current.body.join("\n").trim() });
      }
      current = { heading: line.trim().slice(2).trim(), body: [] };
    } else {
      current.body.push(line);
    }
  }

  if (current.body.join("").trim() || current.heading != null) {
    result.push({ heading: current.heading, body: current.body.join("\n").trim() });
  }

  return result.length ? result : [{ heading: null, body: raw.trim() }];
}

/**
 * Compares old vs new section arrays.
 * Returns new sections annotated with status: "new" | "changed" | "unchanged"
 */
export function diffSections(oldSections, newSections) {
  const oldMap = new Map(oldSections.map(s => [s.heading ?? "__ROOT__", s.body]));
  return newSections.map(s => {
    const key = s.heading ?? "__ROOT__";
    if (!oldMap.has(key))              return { ...s, status: "new" };
    if (oldMap.get(key) !== s.body)    return { ...s, status: "changed" };
    return { ...s, status: "unchanged" };
  });
}

// ── Misc helpers ──────────────────────────────────────────────
export function parseWords(text) {
  return text.trim().split(/\s+/).filter(Boolean);
}

export function formatTime(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export function estimateReadTime(wordCount, speed) {
  const wpm = speed * 12;
  if (wpm <= 0 || wordCount <= 0) return null;
  return Math.ceil((wordCount / wpm) * 60);
}

export function genRoomId() {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}